import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '../../../../../RoutesWithAuth';
import axios from '../../../../common/AxiosConfig';
import { makeStyles } from "@mui/styles";
import { Card, CardContent, Typography, Grid, Box, IconButton, Snackbar, Alert } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LoadingIndicator } from '../../reports/reconcilationreport/MuiStyled';

const useStyles = makeStyles(() => ({
    linksWrapper: {
        marginTop: '4px',
        marginRight: '15px',
        height: 'calc(100% - 100px)',
        padding: '20px'
    },
    linkCard: {
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '1px solid #e0e0e0',
        borderRadius: '10px',
        '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
            borderColor: '#1976d2'
        }
    },
    linkCardContent: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px !important',
        '&:last-child': {
            paddingBottom: '16px !important'
        }
    },
    iconWrapper: {
        backgroundColor: '#e3f2fd',
        borderRadius: '50%',
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '16px',
        color: '#1976d2'
    },
    linkTextWrapper: {
        flex: 1
    },
    linkName: {
        fontWeight: 600,
        color: '#1976d2',
        marginBottom: '4px',
        fontSize: '1rem'
    },
    linkUrl: {
        color: '#666',
        fontSize: '0.875rem',
        wordBreak: 'break-all'
    },
    launchIcon: {
        color: '#1976d2',
        fontSize: '1.25rem'
    },
    copyButton: {
        marginLeft: '8px',
        padding: '6px',
        '&:hover': {
            backgroundColor: '#e3f2fd'
        }
    },
    actionsWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
        color: '#666'
    },
    header: {
        marginBottom: '20px',
        color: '#333',
        fontWeight: 600
    }
}));

function Links() {
    const [appsData, setAppsData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const classes = useStyles();

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken]);

    const fetchData = () => {
        setIsLoading(true);
        axios.get(`/apps/get_app_links?app_segment=admin-catalog-links`, {
            headers: {
                Authorization: authToken
            }
        }).then(res => {
            setIsLoading(false);
            // Ensure data is an array and has proper structure
            const links = Array.isArray(res.data) ? res.data : [];
            setAppsData(links);
        }).catch(err => {
            setIsLoading(false);
            console.error('Failed to fetch links:', err);
            setAppsData([]); // Set empty array on error
        });
    };

    const handleLinkClick = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleCopyLink = (e, url, name) => {
        e.stopPropagation();
        navigator.clipboard.writeText(url)
            .then(() => {
                setSnackbarMessage(`Link copied: ${name}`);
                setSnackbarOpen(true);
            })
            .catch((err) => {
                console.error('Failed to copy link:', err);
            });
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const displayLinks = appsData.length > 0 ? appsData : [];

    return (
        <div className={classes.linksWrapper}>
            {isLoading && (<LoadingIndicator />)}

            {displayLinks.length === 0 ? (
                <Box className={classes.emptyState}>
                    <LinkIcon style={{ fontSize: '4rem', color: '#ccc', marginBottom: '16px' }} />
                    <Typography variant="h6" color="textSecondary">
                        No links available
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {displayLinks.map((link, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card 
                                className={classes.linkCard}
                                onClick={() => handleLinkClick(link.app_link)}
                            >
                                <CardContent className={classes.linkCardContent}>
                                    <div className={classes.iconWrapper}>
                                        <LinkIcon />
                                    </div>
                                    <div className={classes.linkTextWrapper}>
                                        <Typography className={classes.linkName}>
                                            {link.link_description || 'Unnamed Link'}
                                        </Typography>
                                        <Typography className={classes.linkUrl}>
                                            {link.app_link ? (link.app_link.length > 50 ? `${link.app_link.substring(0, 50)}...` : link.app_link) : 'No URL'}
                                        </Typography>
                                    </div>
                                    <div className={classes.actionsWrapper}>
                                        <IconButton
                                            className={classes.copyButton}
                                            onClick={(e) => handleCopyLink(e, link.app_link, link.link_description)}
                                            size="large"
                                            title="Copy link"
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                        <LaunchIcon className={classes.launchIcon} />
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%', top: '165px', right : '30px'}}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Links;